import {
  TrendingUp,
  DollarSign
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { TabsContent } from "../../../components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

const AnalyticsTab = ({ revenueData }) => {
  return (
    <TabsContent value="analytics" className="mt-0 space-y-8 pb-12 outline-none">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
         <Card className="border shadow-none rounded-xl bg-white dark:bg-slate-800 overflow-hidden transition-all hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Monthly Recurring Revenue</CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              <div className="text-3xl font-extrabold tracking-tight">€{revenueData?.mrr?.toFixed(2) || "0.00"}</div>
              <div className="mt-4 flex items-center space-x-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100/50 text-emerald-600 w-fit">
                  <TrendingUp size={10} />
                  <span>+12.4%</span>
              </div>
            </CardContent>
         </Card>

         <Card className="border shadow-none rounded-xl bg-white dark:bg-slate-800 overflow-hidden transition-all hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Customer LTV</CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              <div className="text-3xl font-extrabold tracking-tight">€{revenueData?.totalLifeTimeValue?.toFixed(2) || "0.00"}</div>
              <div className="mt-4">
                <Badge variant="success" className="text-[10px] font-bold rounded-lg px-2 py-0.5">HEALTHY GROWTH</Badge>
              </div>
            </CardContent>
         </Card>

         <Card className="border shadow-none rounded-xl bg-white dark:bg-slate-800 overflow-hidden transition-all hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Churn Reasons</CardTitle>
            </CardHeader>
            <CardContent className="h-48 pb-6">
              {revenueData?.churn?.topReasons?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={revenueData.churn.topReasons}
                    margin={{ left: -20, right: 20 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="label"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={{fontSize: 9, fontBold: '700', fill: '#64748b'}}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '10px' }}
                      cursor={{fill: 'rgba(var(--primary-rgb), 0.05)'}}
                    />
                    <Bar
                      dataKey="value"
                      fill="hsl(var(--primary))"
                      radius={[0, 4, 4, 0]}
                      barSize={12}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-xs text-muted-foreground italic">Keine Kündigungen erfasst.</p>
                </div>
              )}
            </CardContent>
         </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          <Card className="col-span-4 border shadow-none bg-white dark:bg-slate-800 rounded-2xl overflow-hidden">
            <CardHeader className="pb-4 border-b border-slate-50 dark:border-slate-700">
              <CardTitle className="text-base font-bold">Abonnenten-Wachstum</CardTitle>
              <CardDescription className="text-xs">Aktive Abos über die letzten 6 Monate</CardDescription>
            </CardHeader>
            <CardContent className="h-64 pt-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData?.growth || []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{fontSize: 10, fill: '#94a3b8'}}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{fontSize: 10, fill: '#94a3b8'}}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                    cursor={{fill: 'rgba(var(--primary-rgb), 0.05)'}}
                  />
                  <Bar
                    dataKey="count"
                    fill="hsl(var(--primary))"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  >
                    {(revenueData?.growth || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fillOpacity={0.4 + (index / (revenueData.growth.length - 1)) * 0.6} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

         <Card className="col-span-3 border shadow-none bg-white dark:bg-slate-800 rounded-2xl overflow-hidden">
            <CardHeader>
              <CardTitle>Letzte Transaktionen</CardTitle>
              <CardDescription>Einzelpaket-Käufe</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {revenueData?.recentPayments?.map((p, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{p.packageName}</span>
                        <span className="text-[10px] text-muted-foreground">@{p.username}</span>
                      </div>
                    </div>
                    <div className="text-right">
                       <p className="text-sm font-bold">€{p.totalPrice.toFixed(2)}</p>
                       <p className="text-[10px] text-muted-foreground">{new Date(p.createdAt).toLocaleDateString('de-DE')}</p>
                    </div>
                  </div>
                ))}
                {!revenueData?.recentPayments?.length && <p className="text-xs text-muted-foreground italic py-10 text-center">Keine Transaktionen gefunden.</p>}
              </div>
            </CardContent>
         </Card>
      </div>
    </TabsContent>
  );
};

export default AnalyticsTab;
